import { useEffect, useRef } from 'react';

type AudioGlobal = typeof globalThis & {
  webkitAudioContext?: typeof AudioContext;
};

export const useAlarmSound = (unacknowledgedCount: number, intervalMs: number = 60000, externalIsMuted: boolean = false) => {
  const audioContextRef = useRef<AudioContext | null>(null);
  const intervalRef = useRef<number | null>(null);

  const playBeep = () => {
    try {
      // Create a new AudioContext if it doesn't exist
      if (!audioContextRef.current) {
        const audioGlobal = globalThis as AudioGlobal;
        const AudioContextCtor = audioGlobal.AudioContext || audioGlobal.webkitAudioContext;
        if (!AudioContextCtor) {
          return;
        }
        audioContextRef.current = new AudioContextCtor();
      }

      const ctx = audioContextRef.current;
      const oscillator = ctx.createOscillator();
      const gainNode = ctx.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(ctx.destination);

      // Configure the beep sound: 800Hz sine wave
      oscillator.frequency.value = 400;

      oscillator.type = 'triangle'; // Softer than square, warmer than sine

      // Create a short beep with fade out
      const now = ctx.currentTime;
      gainNode.gain.setValueAtTime(0.15, now);
      gainNode.gain.exponentialRampToValueAtTime(0.01, now + 0.3);

      // Play for 300ms
      oscillator.start(now);
      oscillator.stop(now + 0.3);

      // Clean up after playing
      oscillator.onended = () => {
        oscillator.disconnect();
        gainNode.disconnect();
      };
    } catch (err) {
      console.info('Error playing alarm sound:', err);
    }
  };

  useEffect(() => {
    // Clear any existing interval
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    // Only set up the interval if there are unacknowledged alarms and not muted
    if (unacknowledgedCount > 0 && !externalIsMuted) {
      // Play immediately on first detection
      playBeep();

      // Then play every interval (default: 1 minute)
      intervalRef.current = globalThis.setInterval(playBeep, intervalMs);

    } else if (externalIsMuted) {
      console.info('[AlarmSound] Sound alerts muted');
    } else {
      console.info('[AlarmSound] No unacknowledged alarms, sound alerts stopped');
    }

    // Cleanup on unmount or when count changes
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [unacknowledgedCount, intervalMs, externalIsMuted]);

  // Cleanup audio context on unmount
  useEffect(() => {
    return () => {
      if (audioContextRef.current) {
        audioContextRef.current.close().catch(() => {});
        audioContextRef.current = null;
      }
    };
  }, []);
};
